import React, { forwardRef } from "react";
import { Box, Typography, Grid } from "@mui/material";
import { largeStyle } from "components/Modals";
import { GOCertificate } from "api/models/Client";
import { grey } from "@mui/material/colors";
import dayjs from "dayjs";

interface DetailedViewModalProps {
  setOpenModal: React.Dispatch<React.SetStateAction<boolean>>;
  certificate: GOCertificate;
}

const DetailedViewModal = forwardRef<HTMLDivElement, DetailedViewModalProps>(
  ({ certificate }) => {
    return (
      <Box sx={largeStyle}>
        <Typography id="modal-modal-title" variant="h5" component="h5">
          Certificate details
        </Typography>
        <Grid container columnSpacing={2} sx={{ mt: "1rem", mb: "2rem" }}>
          <Grid item sm={9}>
            <Box>
              <Typography variant="body2" color={grey[600]} sx={{ mb: 0.5 }}>
                Certificates number (from - to)
              </Typography>
              <Typography component="p" fontSize="0.9rem">
                {certificate.certificateNumberStart +
                  " - " +
                  certificate.certificateNumberEnd}
              </Typography>
            </Box>
          </Grid>
          <Grid item sm={3}>
            <Box>
              <Typography variant="body2" color={grey[600]} sx={{ mb: 0.5 }}>
                Country
              </Typography>
              <Typography component="p" fontSize="0.9rem">
                {certificate.country}
              </Typography>
            </Box>
          </Grid>
        </Grid>
        <Grid container sx={{ mb: "2rem" }}>
          <Grid item sm={12}>
            <Box>
              <Typography variant="body2" color={grey[600]} sx={{ mb: 0.5 }}>
                Technology and fuel
              </Typography>
              <Typography component="p" fontSize="0.9rem">
                {certificate.technology.name}
              </Typography>
              <Typography component="p" fontSize="0.9rem">
                {certificate.fuel.name}
              </Typography>
            </Box>
          </Grid>
        </Grid>
        <Grid container sx={{ mb: "2rem" }}>
          <Grid item sm={3}>
            <Box>
              <Typography variant="body2" color={grey[600]} sx={{ mb: 0.5 }}>
                Issue date
              </Typography>
              <Typography component="p" fontSize="0.9rem">
                {dayjs(certificate.issueDate).format("DD.MM.YYYY")}
              </Typography>
            </Box>
          </Grid>
          <Grid item sm={3}>
            <Box>
              <Typography variant="body2" color={grey[600]} sx={{ mb: 0.5 }}>
                Expiry date
              </Typography>
              <Typography component="p" fontSize="0.9rem">
                {dayjs(certificate.expiryDate).format("DD.MM.YYYY")}
              </Typography>
            </Box>
          </Grid>
          <Grid item sm={3}>
            <Box>
              <Typography variant="body2" color={grey[600]} sx={{ mb: 0.5 }}>
                Production period
              </Typography>
              <Typography component="p" fontSize="0.9rem">
                {dayjs
                  .tz(certificate.productionStart, "Europe/Tallinn")
                  .format("DD.MM.YYYY") +
                  " -  " +
                  dayjs
                    .tz(certificate.productionEnd, "Europe/Tallinn")
                    .format("DD.MM.YYYY")}
              </Typography>
            </Box>
          </Grid>
          {certificate.transferDeadline && (
            <Grid item sm={3}>
              <Box>
                <Typography variant="body2" color={grey[600]} sx={{ mb: 0.5 }}>
                  Transfer deadline
                </Typography>
                <Typography component="p" fontSize="0.9rem">
                  {dayjs(certificate.transferDeadline).format("DD.MM.YYYY")}
                </Typography>
              </Box>
            </Grid>
          )}
        </Grid>
        <Grid container sx={{ mb: "2rem" }}>
          <Grid item sm={3}>
            <Box>
              <Typography variant="body2" color={grey[600]} sx={{ mb: 0.5 }}>
                Certificates amount
              </Typography>
              <Typography component="p" fontSize="0.9rem">
                {certificate.amount}
              </Typography>
            </Box>
          </Grid>
          <Grid item sm={3}>
            <Box>
              <Typography variant="body2" color={grey[600]} sx={{ mb: 0.5 }}>
                Production support
              </Typography>
              <Typography component="p" fontSize="0.9rem">
                {certificate.productionSupport ? "Yes" : "No"}
              </Typography>
            </Box>
          </Grid>
          <Grid item sm={3}>
            <Box>
              <Typography variant="body2" color={grey[600]} sx={{ mb: 0.5 }}>
                Investment support
              </Typography>
              <Typography component="p" fontSize="0.9rem">
                {certificate.investmentSupport ? "Yes" : "No"}
              </Typography>
            </Box>
          </Grid>
        </Grid>
        <Grid container>
          {certificate.productionDeviceName && (
            <Grid item sm={3}>
              <Box>
                <Typography variant="body2" color={grey[600]} sx={{ mb: 0.5 }}>
                  Production device name
                </Typography>
                <Typography component="p" fontSize="0.9rem">
                  {certificate.productionDeviceName}
                </Typography>
              </Box>
            </Grid>
          )}
          {certificate.productionDeviceOutput && (
            <Grid item sm={3}>
              <Box>
                <Typography variant="body2" color={grey[600]} sx={{ mb: 0.5 }}>
                  Production device output
                </Typography>
                <Typography component="p" fontSize="0.9rem">
                  {certificate.productionDeviceOutput}
                </Typography>
              </Box>
            </Grid>
          )}
          {certificate.deviceLocation && (
            <Grid item sm={5}>
              <Box>
                <Typography variant="body2" color={grey[600]} sx={{ mb: 0.5 }}>
                  Device location
                </Typography>
                <Typography component="p" fontSize="0.9rem">
                  {certificate.deviceLocation}
                </Typography>
              </Box>
            </Grid>
          )}
        </Grid>
      </Box>
    );
  },
);

export default DetailedViewModal;
